import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { portfolioItems, categories } from '../data/mock';
import { Play, ChevronLeft as PrevIcon, ChevronRight as NextIcon, X, Maximize2 } from 'lucide-react';
import { MovingBG } from './MovingBG';
import { FuturisticBG } from './FuturisticBG';

const ITEMS_PER_PAGE = 10;

/* ─────────────────────────────────────────────────────────────
   useInView — IntersectionObserver hook
───────────────────────────────────────────────────────────── */
const useInView = (rootMargin = '80px') => {
  const ref    = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.1, rootMargin }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [rootMargin]);
  return [ref, inView];
};

/* ─────────────────────────────────────────────────────────────
   Category helpers
───────────────────────────────────────────────────────────── */
const categoryColors = {
  Car:        'from-orange-500 to-amber-600',
  Bike:       'from-red-500 to-red-700',
  Event:      'from-purple-500 to-purple-700',
  Personal:   'from-green-500 to-green-700',
  Commercial: 'from-yellow-500 to-orange-600',
  All:        'from-orange-500 to-amber-500',
};
const colorsFor = (cat) => categoryColors[cat] || 'from-orange-500 to-amber-500';

const FEATURED = {
  Car:        [11, 83, 1],
  Bike:       [60, 8, 21],
  Event:      [26, 3, 24],
  Personal:   [30, 87, 68],
  Commercial: [80, 54, 47],
};
const getFeaturedItems = () => {
  const rows = Object.values(FEATURED).map(ids =>
    ids.map(id => portfolioItems.find(p => p.id === id)).filter(Boolean)
  );
  const result = [];
  for (let i = 0; i < 3; i++) rows.forEach(row => { if (row[i]) result.push(row[i]); });
  return result;
};

const NEW_RELEASE_IDS = new Set([
  2, 11, 22, 24, 30, 37, 46, 52, 68, 86, 87, 88, 89,
  1, 5, 26, 28, 33, 38, 43, 48, 53, 58, 63, 69, 73, 75, 79, 81, 83,
]);

/* ─────────────────────────────────────────────────────────────
   GridVideoCard — Optimized for bandwidth
   - WebM thumbnails: Short looping video preview (3-5 secs)
   - WebP/JPG thumbnails: Static/animated image
   - Hover: Zoom effect
   - Click: Opens modal with full video
───────────────────────────────────────────────────────────── */

const GridVideoCard = ({ item, onOpenModal, isNewRelease }) => {
  const [containerRef, inView] = useInView('120px');
  const [isLoaded, setIsLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  
  // Check thumbnail type
  const isVideoThumb = item.thumbnail?.endsWith('.webm') || item.thumbnail?.endsWith('.mp4');
  const isAnimatedThumb = item.thumbnail?.endsWith('.webp');
  
  return (
    <div
      ref={containerRef}
      className="group relative aspect-video rounded-xl overflow-hidden cursor-pointer bg-gray-900"
      onClick={() => onOpenModal(item)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid={`grid-video-${item.id}`}
    >
      {/* Video Thumbnail (WebM) - loops automatically */}
      {inView && item.thumbnail && isVideoThumb && (
        <video 
          src={item.thumbnail}
          autoPlay
          loop
          muted
          playsInline
          onLoadedData={() => setIsLoaded(true)}
          className={`absolute inset-0 w-full h-full object-cover transition-all duration-300 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          } ${isHovered ? 'scale-105' : 'scale-100'}`}
        />
      )}
      
      {/* Image Thumbnail (JPEG/WebP) */}
      {inView && item.thumbnail && !isVideoThumb && (
        <img 
          src={item.thumbnail} 
          alt={item.title}
          loading="lazy"
          onLoad={() => setIsLoaded(true)}
          className={`absolute inset-0 w-full h-full object-cover transition-all duration-300 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          } ${isHovered ? 'scale-105' : 'scale-100'}`}
        />
      )}
      
      {/* Loading skeleton */}
      {(!isLoaded || !item.thumbnail) && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
          <div className="w-7 h-7 border-2 border-orange-500/40 border-t-orange-500 rounded-full animate-spin" />
        </div>
      )}

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
      
      {/* Category badge */}
      <div className="absolute top-1.5 left-1.5">
        <span className={`px-1.5 py-0.5 bg-gradient-to-r ${colorsFor(item.category)} rounded text-[10px] font-bold text-white`}>
          {item.category}
        </span>
      </div>
      
      {/* Play button on hover */}
      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <div className="w-9 h-9 bg-white/25 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/50">
          <Play className="w-3.5 h-3.5 text-white ml-0.5" />
        </div>
      </div>
      
      {/* NEW RELEASE banner */}
      {isNewRelease && (
        <div className="absolute bottom-0 left-0 right-0 overflow-hidden h-[18px] bg-gradient-to-r from-orange-600/90 to-amber-500/90">
          <div className="marquee-track flex whitespace-nowrap items-center h-full">
            {Array(6).fill(null).map((_, i) => (
              <span key={`new-release-${item.id}-${i}`} className="inline-flex items-center gap-2 pr-5 text-[9px] font-black text-white uppercase tracking-widest">★ NEW RELEASE</span>
            ))}
          </div>
        </div>
      )}
      
      {/* Title for non-NEW RELEASE items */}
      {!isNewRelease && (
        <div className="absolute bottom-0 left-0 right-0 p-2">
          <p className="text-white text-xs font-semibold leading-tight line-clamp-1">{item.title}</p>
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   Main Portfolio component
───────────────────────────────────────────────────────────── */
export const Portfolio = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [currentIndex, setCurrentIndex]         = useState(0);
  const [isAutoPlaying, setIsAutoPlaying]       = useState(true);
  const [modalItem, setModalItem]               = useState(null);
  const [showCardInfo, setShowCardInfo]         = useState(false);
  const [gridPage, setGridPage]                 = useState(1);

  const carouselVideoRefs = useRef({});
  const modalVideoRef     = useRef(null);
  const carouselLoop      = useRef({});
  const transitionPending = useRef(false);

  const carouselItems = getFeaturedItems();
  const filteredItems = portfolioItems.filter(
    item => selectedCategory === 'All' || item.category === selectedCategory
  );
  const totalPages     = Math.ceil(filteredItems.length / ITEMS_PER_PAGE);
  const paginatedItems = filteredItems.slice((gridPage - 1) * ITEMS_PER_PAGE, gridPage * ITEMS_PER_PAGE);

  /* ── Carousel position helpers ── */
  const getItemPosition = useCallback((index) => {
    const diff  = index - currentIndex;
    const total = carouselItems.length;
    let pos = diff;
    if (diff >  total / 2) pos = diff - total;
    if (diff < -total / 2) pos = diff + total;
    return pos;
  }, [currentIndex, carouselItems.length]);

  /* ── Carousel smart video management ──
     Only load src for positions ±2.
     Only play at position 0.
     Preload positions ±1 (loaded, paused, ready).
     Clear src for far slides to save memory.
  ── */
  useEffect(() => {
    carouselItems.forEach((item, idx) => {
      const video = carouselVideoRefs.current[idx];
      if (!video) return;

      const pos = getItemPosition(idx);
      const absPos = Math.abs(pos);

      /* Remove old timeupdate loop for this video */
      if (carouselLoop.current[idx]) {
        video.removeEventListener('timeupdate', carouselLoop.current[idx]);
        delete carouselLoop.current[idx];
      }

      if (absPos > 2) {
        /* Far slide — clear src to free memory */
        if (video.getAttribute('data-loaded')) {
          video.pause();
          video.src = '';
          video.load();
          video.removeAttribute('data-loaded');
        }
        return;
      }

      /* Load src if not yet loaded */
      if (!video.getAttribute('data-loaded')) {
        video.src     = item.videoUrl;
        video.preload = absPos <= 1 ? 'auto' : 'metadata';
        video.load();
        video.setAttribute('data-loaded', '1');
      }

      if (pos === 0) {
        /* CENTER — play with 10-second loop */
        const loop = () => { if (video.currentTime >= 10) video.currentTime = 0; };
        video.addEventListener('timeupdate', loop);
        carouselLoop.current[idx] = loop;

        const ready = () => { video.play().catch(() => {}); };
        if (video.readyState >= 2) {
          ready();
        } else {
          video.addEventListener('canplay', ready, { once: true });
        }
      } else if (absPos === 1) {
        /* ADJACENT — loaded, paused at start, ready to play instantly */
        const prepare = () => { video.currentTime = 0; video.pause(); };
        if (video.readyState >= 1) {
          prepare();
        } else {
          video.addEventListener('loadedmetadata', prepare, { once: true });
        }
      } else {
        /* pos ±2 — just load metadata, stay paused */
        video.pause();
      }
    });

    return () => {
      /* Cleanup loops on index change */
      Object.entries(carouselLoop.current).forEach(([idx, loop]) => {
        const video = carouselVideoRefs.current[idx];
        if (video && loop) video.removeEventListener('timeupdate', loop);
      });
      carouselLoop.current = {};
    };
  }, [currentIndex, carouselItems, getItemPosition]);

  /* ── Carousel auto-advance ── */
  useEffect(() => {
    if (!isAutoPlaying || carouselItems.length === 0) return;
    const interval = setInterval(() => {
      setCurrentIndex(p => (p + 1) % carouselItems.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, carouselItems.length]);

  /* ── Modal ── */
  useEffect(() => {
    const fn = (e) => { if (e.key === 'Escape') setModalItem(null); };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, []);

  useEffect(() => {
    setIsAutoPlaying(!modalItem);
    if (modalItem && modalVideoRef.current) modalVideoRef.current.play().catch(() => {});
  }, [modalItem]); // modalVideoRef is a stable ref, setIsAutoPlaying is a stable setter

  /* ── Category / page reset ── */
  useEffect(() => { setGridPage(1); setShowCardInfo(false); }, [selectedCategory]); // setters are stable
  useEffect(() => { setShowCardInfo(false); }, [currentIndex]); // setter is stable

  /* ── Carousel style ── */
  const getItemStyle = (index) => {
    const pos = getItemPosition(index);
    const abs = Math.abs(pos);

    if (abs > 2) return {
      transform: `translateX(calc(-50% + ${pos * 72}%)) translateY(-55%) scale(0.35)`,
      opacity: 0, zIndex: 0, filter: 'blur(20px)', visibility: 'hidden',
    };
    if (pos === 0) return {
      transform: 'translateX(-50%) translateY(-55%) scale(1.14)',
      opacity: 1, zIndex: 30, filter: 'blur(0px)',
      boxShadow: '0 0 40px rgba(249,115,22,0.6), 0 0 80px rgba(245,158,11,0.28), 0 24px 48px rgba(0,0,0,0.8)',
    };
    return {
      transform: `translateX(calc(-50% + ${pos * 70}%)) translateY(-55%) scale(${Math.max(0.68, 1 - abs * 0.2)})`,
      opacity: Math.max(0.35, 1 - abs * 0.32),
      zIndex: 20 - abs,
      filter: `blur(${Math.min(abs * 3, 7)}px)`,
      boxShadow: '0 8px 20px rgba(0,0,0,0.6)',
    };
  };

  const handleCardClick = (item) => {
    if (showCardInfo) {
      setModalItem(item);
    } else {
      setShowCardInfo(true);
      setIsAutoPlaying(false);
      setTimeout(() => { setShowCardInfo(false); setIsAutoPlaying(true); }, 6000);
    }
  };

  /* ─────────────────────────────────────────────────────────── */
  return (
    <section id="portfolio" className="py-24 relative overflow-hidden" style={{ background: '#050505' }}>
      {/* Matching background with Meet The Team section */}
      <MovingBG />
      <FuturisticBG density={10} />

      {/* Geometric background - same as Contact section */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: 'repeating-linear-gradient(45deg, #f97316 0px, #f97316 1px, transparent 0px, transparent 50%)',
          backgroundSize: '40px 40px'
        }} />
        <div className="absolute top-1/4 left-0 w-64 h-64 bg-orange-600/12 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-0 w-64 h-64 bg-amber-600/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-0 right-0 h-px bg-gradient-to-r from-transparent via-orange-500/20 to-transparent" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            <span className="text-white">Our </span>
            <span className="bg-gradient-to-r from-orange-400 to-amber-500 bg-clip-text text-transparent">Portfolio</span>
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">Showcase of our finest work across different categories</p>
          <div className="w-24 h-1 bg-gradient-to-r from-orange-500 to-amber-500 mx-auto mt-6" />
        </div>

        {/* ── CAROUSEL ── */}
        {carouselItems.length > 0 && (
          <div className="relative mb-14">
            <div className="relative h-[280px] sm:h-[360px] md:h-[460px] flex items-center justify-center">
              <div className="relative w-full h-full">
                {carouselItems.map((item, index) => {
                  const style   = getItemStyle(index);
                  const pos     = getItemPosition(index);
                  const isCenter = pos === 0;
                  const isVisible = Math.abs(pos) <= 2;

                  return (
                    <div
                      key={`c-${item.id}`}
                      className="absolute top-1/2 left-1/2 w-[72vw] max-w-[400px] cursor-pointer rounded-3xl"
                      style={{
                        ...style,
                        transition: 'transform 0.65s cubic-bezier(0.34,1.3,0.64,1), opacity 0.55s ease, filter 0.55s ease, box-shadow 0.65s ease',
                        willChange: 'transform, opacity, filter',
                        pointerEvents: isCenter ? 'auto' : 'none',
                      }}
                      onClick={() => isCenter && handleCardClick(item)}
                    >
                      <div
                        className="relative aspect-video rounded-3xl overflow-hidden"
                        style={isCenter ? {
                          WebkitBoxReflect: 'below 5px linear-gradient(transparent 48%, rgba(0,0,0,0.55))',
                        } : {
                          WebkitBoxReflect: 'below 3px linear-gradient(transparent 52%, rgba(0,0,0,0.28))',
                        }}
                      >
                        {/* Video element — always rendered for ±2, cleared for far slides */}
                        <video
                          ref={el => { carouselVideoRefs.current[index] = el; }}
                          muted
                          playsInline
                          preload="none"
                          loop
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

                        {/* Category badge */}
                        <div className="absolute top-3 left-3">
                          <span className={`px-2 py-0.5 bg-gradient-to-r ${colorsFor(item.category)} rounded-full text-xs font-bold text-white`}>
                            {item.category}
                          </span>
                        </div>

                        {/* Center: play/info overlay */}
                        {isCenter && (
                          <>
                            {!showCardInfo && (
                              <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white/15 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/40 hover:bg-white/25 transition-all duration-300">
                                  <Play className="w-5 h-5 text-white ml-0.5" />
                                </div>
                              </div>
                            )}
                            {showCardInfo && (
                              <div className="absolute inset-0 bg-black/50 flex flex-col justify-end animate-[fadeIn_0.3s_ease]">
                                <div className="p-3 sm:p-5">
                                  <p className="text-xs text-blue-400 font-semibold mb-1 uppercase tracking-wider">{item.category}</p>
                                  <h3 className="text-base sm:text-xl font-bold text-white mb-1">{item.title}</h3>
                                  <p className="text-xs text-gray-300 mb-3">{item.description}</p>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setModalItem(item); }}
                                    className="flex items-center gap-2 px-4 py-2 bg-white text-black rounded-full text-xs font-bold hover:bg-gray-200 transition-all"
                                  >
                                    <Maximize2 className="w-3 h-3" /> Watch Full
                                  </button>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Progress bar */}
            <div className="flex items-center justify-center gap-3 mt-6">
              <div className="w-40 sm:w-56 h-0.5 bg-white/15 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-orange-500 to-amber-400 rounded-full transition-all duration-700"
                  style={{ width: `${((currentIndex + 1) / carouselItems.length) * 100}%` }}
                />
              </div>
            </div>
          </div>
        )}

        {/* ── CATEGORY FILTER ── */}
        <div className="flex flex-wrap justify-center gap-3 mb-8">
          {categories.map(cat => (
            <button key={cat} onClick={() => setSelectedCategory(cat)}
              className={`px-5 py-2.5 rounded-full font-semibold text-sm transition-all duration-300 ${
                selectedCategory === cat
                  ? `bg-gradient-to-r ${colorsFor(cat)} text-white shadow-lg scale-105`
                  : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-white/10'
              }`}>
              {cat}
              <span className="ml-2 text-xs opacity-70">({portfolioItems.filter(i => cat === 'All' || i.category === cat).length})</span>
            </button>
          ))}
        </div>

        {/* ── GRID ── */}
        {filteredItems.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-1 h-6 bg-gradient-to-b from-orange-500 to-amber-400 rounded-full" />
                <h3 className="text-lg font-bold text-white">
                  All Videos
                  <span className="ml-2 text-sm text-gray-400 font-normal">({filteredItems.length})</span>
                </h3>
              </div>
              <span className="text-sm text-gray-500">Page {gridPage} / {totalPages}</span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3">
              {paginatedItems.map((item) => (
                <GridVideoCard
                  key={`${gridPage}-${item.id}`}
                  item={item}
                  onOpenModal={setModalItem}
                  isNewRelease={NEW_RELEASE_IDS.has(item.id) || item.category === 'Personal'}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 mt-8">
                <button
                  onClick={() => setGridPage(p => Math.max(1, p - 1))}
                  disabled={gridPage === 1}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-gray-400 hover:bg-white/10 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all text-sm"
                >
                  <PrevIcon className="w-4 h-4" /> Prev
                </button>
                <div className="flex gap-1.5">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                    <button
                      key={p}
                      onClick={() => setGridPage(p)}
                      className={`w-8 h-8 rounded-full text-sm font-semibold transition-all ${
                        p === gridPage
                          ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white'
                          : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-white/10'
                      }`}
                    >{p}</button>
                  ))}
                </div>
                <button
                  onClick={() => setGridPage(p => Math.min(totalPages, p + 1))}
                  disabled={gridPage === totalPages}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-gray-400 hover:bg-white/10 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all text-sm"
                >
                  Next <NextIcon className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── MODAL ── */}
      {modalItem && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4"
          style={{ background: 'rgba(0,0,0,0.95)' }}
          onClick={(e) => e.target === e.currentTarget && setModalItem(null)}
        >
          <div
            className="relative w-full max-w-4xl animate-[fadeIn_0.3s_ease]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setModalItem(null)}
              className="absolute -top-10 sm:-top-12 right-0 w-9 h-9 sm:w-10 sm:h-10 bg-white/20 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-all duration-200 z-10"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="relative aspect-video rounded-xl sm:rounded-2xl overflow-hidden bg-black">
              <video
                ref={modalVideoRef}
                src={modalItem.videoUrl}
                controls
                autoPlay
                preload="auto"
                playsInline
                className="w-full h-full object-contain"
              />
            </div>
            <div className="mt-3 sm:mt-4 px-2">
              <div className="flex items-center gap-3 mb-2">
                <span className={`px-3 py-1 bg-gradient-to-r ${colorsFor(modalItem.category)} rounded-full text-xs font-bold text-white uppercase`}>
                  {modalItem.category}
                </span>
                <span className="text-gray-400 text-sm">{modalItem.description}</span>
              </div>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white">{modalItem.title}</h2>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
